'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useTranslation } from 'react-i18next';

interface Suggestion {
  labelKey: string;
  promptKey: string;
}

interface InputSuggestionsProps {
  lastToolName?: string;
  hasResults?: boolean;
  page?: string;
  onSelect: (prompt: string) => void;
}

// ── Generic tool suggestions (when results exist) ──
const TOOL_SUGGESTIONS: Record<string, Suggestion[]> = {
  catalog_search: [
    { labelKey: 'howMuchImageryCost', promptKey: 'howMuchImageryCostPrompt' },
    { labelKey: 'narrowHighRes', promptKey: 'narrowHighResPrompt' },
    { labelKey: 'compareProviders', promptKey: 'compareProvidersPrompt' },
  ],
  pricing_calculator: [
    { labelKey: 'medResPrice', promptKey: 'medResPricePrompt' },
    { labelKey: 'showAvailableImagery', promptKey: 'showAvailableImageryPrompt' },
    { labelKey: 'productsPriceRange', promptKey: 'productsPriceRangePrompt' },
  ],
  offerings_browser: [
    { labelKey: 'searchUsingProduct', promptKey: 'searchUsingProductPrompt' },
    { labelKey: 'topProductCost', promptKey: 'topProductCostPrompt' },
  ],
  project_status: [
    { labelKey: 'recentOrderDetails', promptKey: 'recentOrderDetailsPrompt' },
    { labelKey: 'searchNewImageryProject', promptKey: 'searchNewImageryProjectPrompt' },
    { labelKey: 'publishAnyArcGIS', promptKey: 'publishAnyArcGISPrompt' },
  ],
  data_product_explainer: [
    { labelKey: 'searchFromProduct', promptKey: 'searchFromProductPrompt' },
    { labelKey: 'productCost', promptKey: 'productCostPrompt' },
  ],
  esri_publisher: [
    { labelKey: 'publishStatus', promptKey: 'publishStatusPrompt' },
    { labelKey: 'otherOrdersPublish', promptKey: 'otherOrdersPublishPrompt' },
  ],
  order_manager: [
    { labelKey: 'currentOrderStatus', promptKey: 'currentOrderStatusPrompt' },
    { labelKey: 'publishOrderArcGIS', promptKey: 'publishOrderArcGISPrompt' },
  ],
  user_context: [
    { labelKey: 'projectsStatus', promptKey: 'projectsStatusPrompt' },
    { labelKey: 'recentOrders', promptKey: 'recentOrdersPrompt' },
  ],
  order_filter: [
    { labelKey: 'filterDifferent', promptKey: 'filterDifferentPrompt' },
    { labelKey: 'showOrderDetails', promptKey: 'showOrderDetailsPrompt' },
    { labelKey: 'publishOrderToArcGIS', promptKey: 'publishOrderToArcGISPrompt' },
  ],
  search_refiner: [
    { labelKey: 'filterProvider', promptKey: 'filterProviderPrompt' },
    { labelKey: 'sortDifferently', promptKey: 'sortDifferentlyPrompt' },
    { labelKey: 'getPriceQuote', promptKey: 'getPriceQuotePrompt' },
  ],
  cart_manager: [
    { labelKey: 'continueBrowsing', promptKey: 'continueBrowsingPrompt' },
    { labelKey: 'pricingCartItems', promptKey: 'pricingCartItemsPrompt' },
    { labelKey: 'reviewCart', promptKey: 'reviewCartPrompt' },
  ],
  data_delivery: [
    { labelKey: 'publishToArcGIS', promptKey: 'publishToArcGISPrompt' },
    { labelKey: 'checkAnotherOrder', promptKey: 'checkAnotherOrderPrompt' },
  ],
  billing_manager: [
    { labelKey: 'showRecentOrders', promptKey: 'showRecentOrdersPrompt' },
    { labelKey: 'checkProjectStatus', promptKey: 'checkProjectStatusPrompt' },
  ],
  tasking_planner: [
    { labelKey: 'taskingPriceQuote', promptKey: 'taskingPriceQuotePrompt' },
    { labelKey: 'compareArchive', promptKey: 'compareArchivePrompt' },
    { labelKey: 'exploreTaskingProducts', promptKey: 'exploreTaskingProductsPrompt' },
  ],
  page_navigator: [{ labelKey: 'whatCanIDo', promptKey: 'whatCanIDoPrompt' }],
};

// ── Tool suggestions when no results found ──
const EMPTY_RESULT_SUGGESTIONS: Record<string, Suggestion[]> = {
  catalog_search: [
    { labelKey: 'broaderDateRange', promptKey: 'broaderDateRangePrompt' },
    { labelKey: 'noResFilter', promptKey: 'noResFilterPrompt' },
    { labelKey: 'productsCoverArea', promptKey: 'productsCoverAreaPrompt' },
  ],
  pricing_calculator: [
    { labelKey: 'differentResTier', promptKey: 'differentResTierPrompt' },
    { labelKey: 'searchAvailableFirst', promptKey: 'searchAvailableFirstPrompt' },
  ],
  offerings_browser: [
    { labelKey: 'searchAllProducts', promptKey: 'searchAllProductsPrompt' },
    { labelKey: 'tryDifferentProvider', promptKey: 'tryDifferentProviderPrompt' },
  ],
  project_status: [
    { labelKey: 'searchSatImagery', promptKey: 'searchSatImageryPrompt' },
    { labelKey: 'exploreProducts', promptKey: 'exploreProductsPrompt' },
  ],
};

// ── Page-specific overrides for tool suggestions (page → tool → suggestions) ──
const PAGE_TOOL_OVERRIDES: Record<string, Record<string, Suggestion[]>> = {
  data_management: {
    catalog_search: [
      { labelKey: 'howMuchImageryCost', promptKey: 'howMuchImageryCostPrompt' },
      { labelKey: 'compareExistingOrders', promptKey: 'compareExistingOrdersPrompt' },
      { labelKey: 'compareProviders', promptKey: 'compareProvidersPrompt' },
    ],
    offerings_browser: [
      { labelKey: 'checkOrderStatus', promptKey: 'checkOrderStatusPrompt' },
      { labelKey: 'searchImageryProducts', promptKey: 'searchImageryProductsPrompt' },
      { labelKey: 'publishOrderToArcGIS', promptKey: 'publishOrderToArcGISPrompt' },
    ],
    pricing_calculator: [
      { labelKey: 'checkExistingOrders', promptKey: 'checkExistingOrdersPrompt' },
      { labelKey: 'tryDifferentRes', promptKey: 'tryDifferentResPrompt' },
    ],
    order_filter: [
      { labelKey: 'filterDifferentCriteria', promptKey: 'filterDifferentCriteriaPrompt' },
      { labelKey: 'showOrderDetails', promptKey: 'showOrderDetailsPrompt' },
      { labelKey: 'publishOrderToArcGIS', promptKey: 'publishOrderToArcGISPrompt' },
    ],
  },
  product_library: {
    offerings_browser: [
      { labelKey: 'compareProducts', promptKey: 'compareProductsPrompt' },
      { labelKey: 'searchImageryFromProduct', promptKey: 'searchImageryFromProductPrompt' },
      { labelKey: 'pricingForProduct', promptKey: 'pricingForProductPrompt' },
    ],
    catalog_search: [
      { labelKey: 'howMuchCost', promptKey: 'howMuchCostPrompt' },
      { labelKey: 'otherProductsArea', promptKey: 'otherProductsAreaPrompt' },
    ],
  },
  archive_search: {
    catalog_search: [
      { labelKey: 'howMuchImageryCost', promptKey: 'howMuchImageryCostPrompt' },
      { labelKey: 'narrowHighRes', promptKey: 'narrowHighResPrompt' },
      { labelKey: 'compareProvidersResults', promptKey: 'compareProvidersResultsPrompt' },
    ],
    offerings_browser: [
      { labelKey: 'searchImageryFromThese', promptKey: 'searchImageryFromThesePrompt' },
      { labelKey: 'compareCapabilities', promptKey: 'compareCapabilitiesPrompt' },
    ],
  },
  orders: {
    offerings_browser: [
      { labelKey: 'checkOrderStatus', promptKey: 'checkOrderStatusPrompt' },
      { labelKey: 'searchNewImagery', promptKey: 'searchNewImageryPrompt' },
    ],
  },
  order_detail: {
    offerings_browser: [
      { labelKey: 'similarImageryPricing', promptKey: 'similarImageryPricingPrompt' },
      { labelKey: 'searchMoreImagery', promptKey: 'searchMoreImageryPrompt' },
    ],
  },
  order_imagery: {
    catalog_search: [
      { labelKey: 'howMuchImageryCost', promptKey: 'howMuchImageryCostPrompt' },
      { labelKey: 'narrowHighRes', promptKey: 'narrowHighResPrompt' },
      { labelKey: 'compareProvidersResults', promptKey: 'compareProvidersResultsPrompt' },
    ],
    offerings_browser: [
      { labelKey: 'searchImageryFromThese', promptKey: 'searchImageryFromThesePrompt' },
      { labelKey: 'pricingForProduct', promptKey: 'pricingForProductPrompt' },
    ],
    search_refiner: [
      { labelKey: 'filterProvider', promptKey: 'filterProviderPrompt' },
      { labelKey: 'getPriceQuote', promptKey: 'getPriceQuotePrompt' },
      { labelKey: 'addToCart', promptKey: 'addToCartPrompt' },
    ],
    cart_manager: [
      { labelKey: 'searchForMoreImagery', promptKey: 'searchForMoreImageryPrompt' },
      { labelKey: 'pricingForCart', promptKey: 'pricingForCartPrompt' },
      { labelKey: 'compareProvidersSearch', promptKey: 'compareProvidersSearchPrompt' },
    ],
  },
};

// ── Page-specific defaults (when no tool was used or tool has no mapping) ──
const PAGE_DEFAULT_SUGGESTIONS: Record<string, Suggestion[]> = {
  data_management: [
    { labelKey: 'showRecentOrders', promptKey: 'showRecentOrdersPrompt' },
    { labelKey: 'publishOrderToArcGIS', promptKey: 'publishOrderToArcGISPrompt' },
    { labelKey: 'filterOrdersByDate', promptKey: 'filterOrdersByDatePrompt' },
  ],
  product_library: [
    { labelKey: 'compareAvailableProducts', promptKey: 'compareAvailableProductsPrompt' },
    { labelKey: 'bestProductForMe', promptKey: 'bestProductForMePrompt' },
    { labelKey: 'getPricingQuote', promptKey: 'getPricingQuotePrompt' },
  ],
  archive_search: [
    { labelKey: 'getPriceQuote', promptKey: 'getPriceQuotePrompt' },
    { labelKey: 'compareProvidersArea', promptKey: 'compareProvidersAreaPrompt' },
    { labelKey: 'exploreProducts', promptKey: 'exploreProductsPrompt' },
  ],
  orders: [
    { labelKey: 'showRecentOrders', promptKey: 'showRecentOrdersPrompt' },
    { labelKey: 'publishToArcGIS', promptKey: 'publishToArcGISPrompt' },
    { labelKey: 'checkDeliveryStatus', promptKey: 'checkDeliveryStatusPrompt' },
  ],
  order_detail: [
    { labelKey: 'checkThisOrderStatus', promptKey: 'checkThisOrderStatusPrompt' },
    { labelKey: 'publishThisOrderArcGIS', promptKey: 'publishThisOrderArcGISPrompt' },
    { labelKey: 'checkDeliveryStatusOrder', promptKey: 'checkDeliveryStatusOrderPrompt' },
  ],
  projects: [
    { labelKey: 'checkProjectStatus', promptKey: 'checkProjectStatusPrompt' },
    { labelKey: 'exploreProducts', promptKey: 'exploreProductsPrompt' },
  ],
  project_detail: [
    { labelKey: 'tellAboutProject', promptKey: 'tellAboutProjectPrompt' },
    { labelKey: 'ordersInProject', promptKey: 'ordersInProjectPrompt' },
  ],
  tasking: [
    { labelKey: 'taskingPricing', promptKey: 'taskingPricingPrompt' },
    { labelKey: 'availableTaskingSatellites', promptKey: 'availableTaskingSatellitesPrompt' },
  ],
  order_imagery: [
    { labelKey: 'searchSatImagery', promptKey: 'searchSatImageryPrompt' },
    { labelKey: 'getPricingQuote', promptKey: 'getPricingQuotePrompt' },
    { labelKey: 'exploreProducts', promptKey: 'exploreProductsPrompt' },
  ],
  approvals: [
    { labelKey: 'showRecentOrders', promptKey: 'showRecentOrdersPrompt' },
    { labelKey: 'checkAccount', promptKey: 'checkAccountPrompt' },
    { labelKey: 'billingInfo', promptKey: 'billingInfoPrompt' },
  ],
  metrics: [
    { labelKey: 'checkProjectStatus', promptKey: 'checkProjectStatusPrompt' },
    { labelKey: 'showRecentOrders', promptKey: 'showRecentOrdersPrompt' },
    { labelKey: 'billingInfo', promptKey: 'billingInfoPrompt' },
  ],
  organization: [
    { labelKey: 'checkAccountRole', promptKey: 'checkAccountRolePrompt' },
    { labelKey: 'teamMembers', promptKey: 'teamMembersPrompt' },
    { labelKey: 'billingInfo', promptKey: 'billingInfoPrompt' },
  ],
  account: [
    { labelKey: 'checkAccount', promptKey: 'checkAccountPrompt' },
    { labelKey: 'billingInfo', promptKey: 'billingInfoPrompt' },
    { labelKey: 'showRecentOrders', promptKey: 'showRecentOrdersPrompt' },
  ],
};

const DEFAULT_SUGGESTIONS: Suggestion[] = [
  { labelKey: 'searchSatImagery', promptKey: 'searchSatImageryPrompt' },
  { labelKey: 'whatProductsAvailable', promptKey: 'whatProductsAvailablePrompt' },
  { labelKey: 'projectsStatus', promptKey: 'projectsStatusPrompt' },
];

export default function InputSuggestions({
  lastToolName,
  hasResults = true,
  page,
  onSelect,
}: InputSuggestionsProps) {
  const { t } = useTranslation('ai-assistant');

  let suggestions: Suggestion[];

  if (lastToolName) {
    if (!hasResults && EMPTY_RESULT_SUGGESTIONS[lastToolName]) {
      suggestions = EMPTY_RESULT_SUGGESTIONS[lastToolName];
    } else if (page && PAGE_TOOL_OVERRIDES[page]?.[lastToolName]) {
      suggestions = PAGE_TOOL_OVERRIDES[page][lastToolName];
    } else if (page && PAGE_DEFAULT_SUGGESTIONS[page]) {
      suggestions = PAGE_DEFAULT_SUGGESTIONS[page];
    } else {
      suggestions = TOOL_SUGGESTIONS[lastToolName] || DEFAULT_SUGGESTIONS;
    }
  } else {
    suggestions = (page && PAGE_DEFAULT_SUGGESTIONS[page]) || DEFAULT_SUGGESTIONS;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', mb: '0.375rem' }}>
      {suggestions.map(s => (
        <Box
          key={s.labelKey}
          onClick={() => onSelect(t(s.promptKey))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            px: '0.75rem',
            py: '0.4rem',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'background-color 0.15s',
            '&:hover': {
              bgcolor: 'rgba(71, 225, 188, 0.08)',
            },
            '&:hover .suggestion-arrow': {
              opacity: 1,
              color: 'var(--primary-color)',
            },
          }}
        >
          <Typography
            sx={{
              color: 'var(--sw-white-color)',
              opacity: 0.6,
              fontSize: '0.8rem',
              lineHeight: 1.3,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {t(s.labelKey)}
          </Typography>
          <ArrowUpwardIcon
            className="suggestion-arrow"
            sx={{
              fontSize: '0.8rem',
              opacity: 0,
              transition: 'opacity 0.15s',
              color: 'var(--sw-white-color)',
              flexShrink: 0,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}
